import { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { getBaseUrl, getJobStatus, cancelJob as cancelJobApi } from './dataApi';
import { debugLogger } from './debugLogger';
import { useAuth } from '../Contexts/AuthContext';

/**
 * Custom hook for WebSocket-based project creation progress tracking.
 * 
 * Features:
 * - Real-time progress updates via WebSocket
 * - Automatic fallback to polling if WebSocket fails
 * - Reconnection handling
 * - JWT authentication
 * 
 * @param {string} jobId - Job ID to track
 * @param {Object} options - Configuration options
 * @param {Function} options.onProgress - Callback for progress updates
 * @param {Function} options.onCompleted - Callback when job completes
 * @param {Function} options.onFailed - Callback when job fails
 * @param {boolean} options.enablePolling - Enable polling fallback (default: true)
 * @param {number} options.pollingInterval - Polling interval in ms (default: 2000)
 * @returns {Object} { progress, status, error, cancelJob, disconnect }
 */
export const useProjectCreationSocket = (jobId, options = {}) => {
  const auth = useAuth();
  const {
    onProgress,
    onCompleted,
    onFailed,
    enablePolling = true,
    pollingInterval = 2000  // Poll every 2 seconds (was 500ms - too aggressive)
  } = options;

  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('pending');
  const [step, setStep] = useState('Initializing');
  const [detail, setDetail] = useState('');
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [usePollingFallback, setUsePollingFallback] = useState(false);

  const socketRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const mountedRef = useRef(true);
  const statusRef = useRef('pending');
  const usePollingFallbackRef = useRef(false);

  // Store callbacks in refs so event handlers always use the latest version
  // without needing to re-register socket events on every render.
  const onProgressRef = useRef(onProgress);
  const onCompletedRef = useRef(onCompleted);
  const onFailedRef = useRef(onFailed);
  useEffect(() => {
    onProgressRef.current = onProgress;
    onCompletedRef.current = onCompleted;
    onFailedRef.current = onFailed;
  });

  /**
   * Get JWT token from auth context
   */
  const getAuthToken = useCallback(() => {
    return auth.activeUser?.accessToken;
  }, [auth.activeUser?.accessToken]);

  /**
   * Poll job status via REST API
   */
  const pollJobStatus = useCallback(async () => {
    if (!jobId || !mountedRef.current) return;

    try {
      const token = getAuthToken();
      if (!token) {
        debugLogger.error('WEBSOCKET', 'No auth token for polling');
        return;
      }

      const result = await getJobStatus(jobId, token);

      if (result?.response) {
        throw new Error(`Polling failed: ${result.response?.status}`);
      }

      const data = result.data;

      if (!mountedRef.current) return;

      // Update state
      setProgress(data.progress || 0);
      setStatus(data.status);
      statusRef.current = data.status;
      setStep(data.step || '');
      setDetail(data.detail || '');

      // Call callbacks via refs to always invoke the latest version
      if (data.status === 'in_progress' && onProgressRef.current) {
        onProgressRef.current(data);
      } else if (data.status === 'completed' && onCompletedRef.current) {
        onCompletedRef.current(data);
        // Stop polling on completion
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      } else if (data.status === 'failed' && onFailedRef.current) {
        setError(data.error);
        onFailedRef.current(data);
        // Stop polling on failure
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      }

      debugLogger.log('WEBSOCKET', 'Polling update', {
        jobId,
        status: data.status,
        progress: data.progress
      });

    } catch (err) {
      debugLogger.error('WEBSOCKET', 'Polling error', err);
    }
  }, [jobId, getAuthToken]);

  /**
   * Start polling fallback
   */
  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) return;

    debugLogger.log('WEBSOCKET', 'Starting polling fallback', { jobId, pollingInterval });
    setUsePollingFallback(true);
    usePollingFallbackRef.current = true;

    // Poll immediately
    pollJobStatus();

    // Then poll at interval
    pollingIntervalRef.current = setInterval(pollJobStatus, pollingInterval);
  }, [jobId, pollingInterval, pollJobStatus]);

  /**
   * Stop polling
   */
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      debugLogger.log('WEBSOCKET', 'Stopped polling');
    }
  }, []);

  /**
   * Connect to WebSocket
   */
  const connectSocket = useCallback(() => {
    if (socketRef.current?.connected) return;

    const token = getAuthToken();
    if (!token) {
      debugLogger.error('WEBSOCKET', 'No auth token available');
      if (enablePolling) {
        startPolling();
      }
      return;
    }

    const baseUrl = getBaseUrl();
    debugLogger.log('WEBSOCKET', 'Connecting to WebSocket', { baseUrl, jobId });

    const socket = io(baseUrl, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5
    });

    socketRef.current = socket;

    // Connection events
    socket.on('connect', () => {
      if (!mountedRef.current) return;
      
      debugLogger.log('WEBSOCKET', 'Connected', { socketId: socket.id });
      setIsConnected(true);
      // Keep polling running as a safety net — only stop it when we actually
      // receive a real event from the WebSocket (job_progress, job_completed, etc.)
      // This prevents the case where the WebSocket "connects" but can't deliver events.

      // Join job room
      socket.emit('join_job', { job_id: jobId });
      
      // Request current status
      socket.emit('get_job_status', { job_id: jobId });
    });

    socket.on('disconnect', (reason) => {
      if (!mountedRef.current) return;
      
      debugLogger.log('WEBSOCKET', 'Disconnected', { reason });
      setIsConnected(false);

      // Start polling fallback if enabled (skip if already in a terminal state)
      if (
        enablePolling &&
        statusRef.current !== 'completed' &&
        statusRef.current !== 'failed' &&
        statusRef.current !== 'cancelled'
      ) {
        startPolling();
      }
    });

    socket.on('connect_error', (err) => {
      debugLogger.error('WEBSOCKET', 'Connection error', err);
      
      // Fallback to polling after connection errors, but only if the job isn't already done
      if (
        enablePolling &&
        !usePollingFallbackRef.current &&
        statusRef.current !== 'completed' &&
        statusRef.current !== 'failed' &&
        statusRef.current !== 'cancelled'
      ) {
        startPolling();
      }
    });

    // Job progress events
    socket.on('job_progress', (data) => {
      if (!mountedRef.current || data.job_id !== jobId) return;

      debugLogger.log('WEBSOCKET', 'Progress update via WebSocket', data);
      // WebSocket is actually delivering events — stop polling to avoid duplicates
      stopPolling();
      setUsePollingFallback(false);
      usePollingFallbackRef.current = false;

      setProgress(data.progress || 0);
      setStatus(data.status || 'in_progress');
      statusRef.current = data.status || 'in_progress';
      setStep(data.step || '');
      setDetail(data.detail || '');

      if (onProgressRef.current) {
        onProgressRef.current(data);
      }
    });

    socket.on('job_completed', (data) => {
      if (!mountedRef.current || data.job_id !== jobId) return;

      debugLogger.log('WEBSOCKET', 'Job completed', data);

      // Stop polling if it was running as a fallback
      stopPolling();

      setProgress(100);
      setStatus('completed');
      statusRef.current = 'completed';
      setStep('Completed');

      if (onCompletedRef.current) {
        onCompletedRef.current(data);
      }

      // Disconnect the socket now that the job has reached a terminal state
      socket.disconnect();
      socketRef.current = null;
    });

    socket.on('job_failed', (data) => {
      if (!mountedRef.current || data.job_id !== jobId) return;

      debugLogger.error('WEBSOCKET', 'Job failed', data);

      // Stop polling if it was running as a fallback
      stopPolling();

      setStatus('failed');
      statusRef.current = 'failed';
      setError(data.error);

      if (onFailedRef.current) {
        onFailedRef.current(data);
      }

      // Disconnect the socket now that the job has reached a terminal state
      socket.disconnect();
      socketRef.current = null;
    });

    socket.on('job_status', (data) => {
      if (!mountedRef.current || data.job_id !== jobId) return;

      debugLogger.log('WEBSOCKET', 'Job status', data);

      setProgress(data.progress || 0);
      setStatus(data.status);
      statusRef.current = data.status;
      setStep(data.step || '');
      setDetail(data.detail || '');

      if (data.error) {
        setError(data.error);
      }
    });

  }, [jobId, getAuthToken, enablePolling, startPolling, stopPolling]);

  /**
   * Disconnect from WebSocket
   */
  const disconnect = useCallback(() => {
    debugLogger.log('WEBSOCKET', 'Disconnecting', { jobId });

    if (socketRef.current) {
      socketRef.current.emit('leave_job', { job_id: jobId });
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    stopPolling();
    setIsConnected(false);
  }, [jobId, stopPolling]);

  /**
   * Cancel job
   */
  const cancelJob = useCallback(async () => {
    if (!jobId) return;

    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No auth token');
      }

      const response = await cancelJobApi(jobId, token);

      // cancelJobApi catches and returns the error object on failure
      if (response instanceof Error || (response?.response && response.response.status >= 400)) {
        const detail = response instanceof Error
          ? response.message
          : `HTTP ${response.response.status}`;
        throw new Error(`Failed to cancel job: ${detail}`);
      }

      debugLogger.log('WEBSOCKET', 'Job cancelled', { jobId });

      statusRef.current = 'cancelled';
      setStatus('cancelled');
      disconnect();

      return true;
    } catch (err) {
      debugLogger.error('WEBSOCKET', 'Cancel job error', err);
      throw err;
    }
  }, [jobId, getAuthToken, disconnect]);

  // Connect on mount, and reconnect if the auth token becomes available after initial mount
  useEffect(() => {
    if (!jobId) return;

    mountedRef.current = true;
    
    // Always start polling immediately — it runs as a reliable fallback even if
    // WebSocket connects, since the Flask dev server may not deliver WS events.
    // Polling stops only when a real WebSocket event is received (job_progress/completed).
    if (enablePolling) {
      startPolling();
    }
    
    connectSocket();

    return () => {
      mountedRef.current = false;
      disconnect();
    };
    // connectSocket and disconnect are intentionally excluded from the dependency array.
    // Their reactive state (status, usePollingFallback) is read via refs so they don't need
    // to be recreated on state changes. Re-running the effect when jobId, enablePolling, or
    // the access token changes ensures we connect/reconnect when the token becomes available.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, enablePolling, auth.activeUser?.accessToken]);

  return {
    progress,
    status,
    step,
    detail,
    error,
    isConnected,
    usePollingFallback,
    cancelJob,
    disconnect
  };
};
